import type { editor } from "monaco-editor";
import type { ToolbarIconInfo } from "@Primitive/ToolbarIconButton";
// ================================================================================================
//Port Keys
export const enum PortKeys {
    InputData = "InputData",
    OutputData = "OutputData",
}

// Define an enumeration for the three theme settings (vs, vs-dark, hc-black)
export enum CodeEditorTheme {
    vs = "vs",
    vsDark = "vs-dark",
    hcBlack = "hc-black",
}

//define port keys with an enumeration
export const enum CodeEditorKeys {
    OnToolbarIconClick = 'onToolbarIconClick',
    OutputData = "OutputData",
    InputData = "InputData"
}

// ================================================================================================
//Data
export interface CodeEditorData {
    theme: {
        editorTheme: CodeEditorTheme;
        toolbarHeight: number;
        toolbarIconSize: number;
        iconColor: string;
        toolbarColor: string;
    };
    icons: {
        alignButton: boolean;
        wrapButton: boolean;
        saveButton: boolean;
        discardButton: boolean;
    };
    customLeftToolbarIcons: ToolbarIconInfo[];
    customCenterToolbarIcons: ToolbarIconInfo[];
    customRightToolbarIcons: ToolbarIconInfo[];

    isStrictSaved?: boolean;
}

// ================================================================================================
//State
export interface CodeEditorState {
    // Settings from port
    settings: ICodeEditorSettings;

    // Wrapping code
    wrapCode: boolean;

    /**
     * Check if validation errors exist
     */
    hasErrors: boolean;
}

// ================================================================================================
//Settings coming to the input port as json file
//-------------------------------------------------------------------------------------------------
export interface ICodeEditorSettings {
    /**
     * Configure whether we allow saving when json is not valid
     */
    isStrictSaved?: boolean;

    /**
     * Configure validation schemas
     */
    schemas?: any;

    /**
     * Icons based on toolbar position
     */
    leftToolbarIcons?: ToolbarIconInfo[];
    centerToolbarIcons?: ToolbarIconInfo[];
    rightToolbarIcons?: ToolbarIconInfo[];

    /**
     * The initial value of the auto created model in the editor.
     */
    defaultValue?: string;

    /**
     * The initial language of the auto created model in the editor. Defaults to 'javascript'.
     */
    language?: string;

    /**
     * Theme to be used for rendering.
     * The current out-of-the-box available themes are: 'vs' (default), 'vs-dark', 'hc-black'.
     * You can create custom themes via `monaco.editor.defineTheme`.
     */
    theme?: string | null;
    /**
     * Value of the auto created model in the editor.
     * If you specify value property, the component behaves in controlled mode. Otherwise, it behaves in uncontrolled mode.
     */
    value?: string | null;

    /**
     * The original value that we need to see the difference with.
     * If you specify originalValue property, the component open in diff mode.
     */
    originalValue?: string;

    /**
     * Refer to Monaco interface {monaco.editor.IEditorConstructionOptions}.
     */
    options?: editor.IEditorOptions;

    /**
     * Add extraLib.
     */
    defaultTypeString?: string;

    /**
     * External definition library
     */
    externalLibrary?: { content: string; filename: string }[];

    /**
     * menu/keyboard actions
     */
    actions?: {
        /**
         * An unique identifier of the contributed action.
         */
        id: string;
        /**
         * A label of the action that will be presented to the user.
         */
        label: string;
        /**
         * Precondition rule.
         */
        precondition?: string;
        /**
         * An array of keybindings for the action.
         */
        keybindings?: number[];
        /**
         * The keybinding rule (condition on top of precondition).
         */
        keybindingContext?: string;
        /**
         * Control if the action should show up in the context menu and where.
         * The context menu of the editor has these default:
         *   navigation - The navigation group comes first in all cases.
         *   1_modification - This group comes next and contains commands that modify your code.
         *   9_cutcopypaste - The last default group with the basic editing commands.
         * You can also create your own group.
         * Defaults to null (don't show in context menu).
         */
        contextMenuGroupId?: string;
        /**
         * Control the order in the context menu group.
         */
        contextMenuOrder?: number;
        /**
         * Method that will be executed when the action is triggered.
         * @param editor The editor instance is passed in as a convenience
         */
        run(editor: any): void | Promise<void>;
    }[];

    decorations?: {
        range: {
            /**
             * Line number on which the range starts (starts at 1).
             */
            readonly startLineNumber: number;
            /**
             * Column on which the range starts in line `startLineNumber` (starts at 1).
             */
            readonly startColumn: number;
            /**
             * Line number on which the range ends.
             */
            readonly endLineNumber: number;
            /**
             * Column on which the range ends in line `endLineNumber`.
             */
            readonly endColumn: number;
        };

        options: {
            /**
             * Should the decoration expand to encompass a whole line.
             */
            isWholeLine?: boolean;

            /**
             * CSS class name describing the decoration.
             */
            className?: string | null;

            /**
             * If set, the decoration will be rendered in the lines decorations with this CSS class name.
             */
            linesDecorationsClassName?: string | null;

            /**
             * If set, the decoration will be rendered in the glyph margin with this CSS class name.
             */
            glyphMarginClassName?: string | null;
        };
    }[];
}

//-------------------------------------------------------------------------------------------------